import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Plus, PencilSimple, Trash, DownloadSimple, MagnifyingGlass, Receipt } from "@phosphor-icons/react";
import { useAuth } from "../../lib/auth";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Pagination from "../../components/Pagination";

const TODAY_ISO = () => new Date().toISOString().slice(0, 10);
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const monthKey = (d) => (!d || d.length < 7) ? "" : d.slice(0, 7);
const monthLabel = (k) => {
  if (!k || k.length < 7) return "—";
  const [y, m] = k.split("-");
  return `${MONTHS[parseInt(m,10)-1] || m} ${y}`;
};
const inr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

function BillDialog({ trigger, initial, onSaved }) {
  const editing = Boolean(initial?.id);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    patient_name: "", phone: "",
    admission_fee: 0, medicine: "", medicine_cost: 0,
    amount_paid: 0, notes: "", bill_date: TODAY_ISO(),
  });

  useEffect(() => {
    if (open) {
      setForm(initial ? {
        patient_name: initial.patient_name || "", phone: initial.phone || "",
        admission_fee: initial.admission_fee ?? 0,
        medicine: initial.medicine || "", medicine_cost: initial.medicine_cost ?? 0,
        amount_paid: initial.amount_paid ?? 0, notes: initial.notes || "",
        bill_date: initial.bill_date || TODAY_ISO(),
      } : {
        patient_name: "", phone: "",
        admission_fee: 0, medicine: "", medicine_cost: 0,
        amount_paid: 0, notes: "", bill_date: TODAY_ISO(),
      });
    }
  }, [open, initial]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const total = Number(form.admission_fee || 0) + Number(form.medicine_cost || 0);
  const balance = total - Number(form.amount_paid || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_name.trim()) { toast.error("Patient name required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        admission_fee: Number(form.admission_fee || 0),
        medicine_cost: Number(form.medicine_cost || 0),
        amount_paid: Number(form.amount_paid || 0),
      };
      if (editing) {
        await api.patch(`/bills/${initial.id}`, payload);
        toast.success("Bill updated");
      } else {
        await api.post("/bills", payload);
        toast.success("Bill created");
      }
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xl bg-cream max-h-[90vh] overflow-y-auto" data-testid="bill-dialog">
        <DialogHeader><DialogTitle className="font-serif-display text-2xl">{editing ? `Edit bill ${initial.bill_no}` : "New bill of entry"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="bill-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Patient name *</Label><Input data-testid="b-name" value={form.patient_name} onChange={set("patient_name")} className="bg-ivory h-11 mt-1" required/></div>
            <div><Label>Phone</Label><Input data-testid="b-phone" value={form.phone} onChange={set("phone")} className="bg-ivory h-11 mt-1"/></div>
            <div><Label>Bill date</Label><Input data-testid="b-date" type="date" value={form.bill_date} onChange={set("bill_date")} className="bg-ivory h-11 mt-1"/></div>
            <div><Label>Admission / Consultation fee (₹)</Label><Input data-testid="b-admission" type="number" min="0" step="0.01" value={form.admission_fee} onChange={set("admission_fee")} className="bg-ivory h-11 mt-1"/></div>
            <div><Label>Medicine cost (₹)</Label><Input data-testid="b-medcost" type="number" min="0" step="0.01" value={form.medicine_cost} onChange={set("medicine_cost")} className="bg-ivory h-11 mt-1"/></div>
            <div className="sm:col-span-2"><Label>Medicine purchased</Label><Textarea data-testid="b-medicine" value={form.medicine} onChange={set("medicine")} className="bg-ivory mt-1 min-h-[80px]" placeholder="e.g., Ashwagandha Churna 100g × 2, Chyawanprash 500g × 1"/></div>
            <div><Label>Amount paid (₹)</Label><Input data-testid="b-paid" type="number" min="0" step="0.01" value={form.amount_paid} onChange={set("amount_paid")} className="bg-ivory h-11 mt-1"/></div>
            <div className="bg-ivory border border-border/50 rounded-xl px-4 flex flex-col justify-center">
              <div className="text-xs text-muted-foreground">Total · Balance</div>
              <div className="font-serif-display text-xl leading-tight mt-1">
                <span className="text-forest">{inr(total)}</span>
                <span className={`ml-3 text-sm ${balance > 0 ? "text-terracotta" : "text-sage"}`}>· {inr(balance)} {balance > 0 ? "due" : balance < 0 ? "change" : "paid"}</span>
              </div>
            </div>
            <div className="sm:col-span-2"><Label>Notes</Label><Textarea data-testid="b-notes" value={form.notes} onChange={set("notes")} className="bg-ivory mt-1 min-h-[60px]" placeholder="Payment mode, remarks…"/></div>
          </div>
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-border px-5 py-2 text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="b-submit" className="rounded-full bg-forest text-cream px-6 py-2.5 text-sm hover:bg-terracotta transition-all disabled:opacity-50">
              {saving ? "Saving…" : (editing ? "Save changes" : "Create bill")}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminBills() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [monthFilter, setMonthFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const load = () => api.get("/bills").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const availableMonths = React.useMemo(() => {
    const set = new Set(rows.map(r => monthKey(r.bill_date)).filter(Boolean));
    return Array.from(set).sort().reverse();
  }, [rows]);

  const filtered = rows.filter(r => {
    if (monthFilter !== "all" && monthKey(r.bill_date) !== monthFilter) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return [r.bill_no, r.patient_name, r.phone, r.medicine, r.notes].some(v => (v || "").toLowerCase().includes(q));
  });

  useEffect(() => { setPage(1); }, [query, monthFilter, pageSize]);
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  const stats = {
    total: filtered.length,
    gross: filtered.reduce((s, r) => s + Number(r.total || 0), 0),
    collected: filtered.reduce((s, r) => s + Number(r.amount_paid || 0), 0),
    outstanding: filtered.reduce((s, r) => s + Math.max(0, Number(r.balance || 0)), 0),
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete bill ${b.bill_no}? Cannot be undone.`)) return;
    try { await api.delete(`/bills/${b.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const printBill = (b) => {
    const doc = new jsPDF({ unit: "pt", format: "a5" });
    const w = doc.internal.pageSize.getWidth();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(15, 59, 32);
    doc.text("YUVA AYURVEDIC CLINIC PRIVATE LIMITED", w/2, 40, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(92, 107, 97);
    doc.text("Prayagraj, UP · info@yuvaayurvedic.com · +91 95190 78666", w/2, 54, { align: "center" });
    doc.setDrawColor(216, 210, 196);
    doc.line(30, 66, w - 30, 66);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    doc.text(`BILL OF ENTRY — ${b.bill_no}`, 30, 86);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    let y = 110;
    const line = (k, v) => { doc.setFont("helvetica","bold"); doc.text(k, 30, y); doc.setFont("helvetica","normal"); doc.text(String(v || "—"), 130, y); y += 16; };
    line("Date:", b.bill_date);
    line("Patient:", b.patient_name);
    if (b.phone) line("Phone:", b.phone);
    y += 6;

    autoTable(doc, {
      startY: y,
      head: [["Item", "Amount (₹)"]],
      body: [
        ["Admission / Consultation fee", Number(b.admission_fee || 0).toFixed(2)],
        [`Medicine${b.medicine ? ` — ${b.medicine}` : ""}`, Number(b.medicine_cost || 0).toFixed(2)],
      ],
      foot: [
        ["Total", Number(b.total || 0).toFixed(2)],
        ["Paid", Number(b.amount_paid || 0).toFixed(2)],
        ["Balance", Number(b.balance || 0).toFixed(2)],
      ],
      styles: { font: "helvetica", fontSize: 9, cellPadding: 5 },
      headStyles: { fillColor: [15, 59, 32], textColor: [247, 245, 240] },
      footStyles: { fillColor: [247, 245, 240], textColor: [15, 59, 32], fontStyle: "bold" },
      columnStyles: { 1: { halign: "right" } },
      margin: { left: 30, right: 30 },
    });

    const endY = doc.lastAutoTable.finalY + 20;
    if (b.notes) {
      doc.setFontSize(8);
      doc.setTextColor(92, 107, 97);
      doc.text(`Notes: ${b.notes}`, 30, endY, { maxWidth: w - 60 });
    }
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text(`Issued by ${b.created_by || "Clinic"} · ${new Date(b.created_at).toLocaleString()}`, w/2, doc.internal.pageSize.getHeight() - 20, { align: "center" });

    doc.save(`bill-${b.bill_no}.pdf`);
  };

  const exportMonth = () => {
    if (filtered.length === 0) { toast.error("No bills to export"); return; }
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const w = doc.internal.pageSize.getWidth();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(15, 59, 32);
    doc.text("Yuva Ayurvedic Clinic Private Limited — Billing Register", 40, 40);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(92, 107, 97);
    doc.text(`${monthFilter === "all" ? "All months" : monthLabel(monthFilter)}  ·  ${filtered.length} bills  ·  Generated ${new Date().toLocaleString()}`, 40, 58);

    autoTable(doc, {
      startY: 80,
      head: [["Bill No", "Date", "Patient", "Phone", "Admission", "Medicine", "Med Cost", "Paid", "Balance"]],
      body: filtered.map(b => [
        b.bill_no, b.bill_date, b.patient_name, b.phone || "",
        Number(b.admission_fee || 0).toFixed(2),
        b.medicine || "",
        Number(b.medicine_cost || 0).toFixed(2),
        Number(b.amount_paid || 0).toFixed(2),
        Number(b.balance || 0).toFixed(2),
      ]),
      foot: [[
        "", "", "", "TOTALS",
        filtered.reduce((s,b) => s + Number(b.admission_fee||0), 0).toFixed(2),
        "",
        filtered.reduce((s,b) => s + Number(b.medicine_cost||0), 0).toFixed(2),
        filtered.reduce((s,b) => s + Number(b.amount_paid||0), 0).toFixed(2),
        filtered.reduce((s,b) => s + Number(b.balance||0), 0).toFixed(2),
      ]],
      styles: { font: "helvetica", fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [15, 59, 32], textColor: [247, 245, 240] },
      footStyles: { fillColor: [247, 245, 240], textColor: [15, 59, 32], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [247, 245, 240] },
      columnStyles: { 4: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" } },
      margin: { left: 40, right: 40 },
    });
    doc.save(`yuva-bills-${monthFilter === "all" ? "all" : monthFilter}-${new Date().toISOString().slice(0,10)}.pdf`);
    toast.success(`Exported ${filtered.length} bills`);
  };

  return (
    <div data-testid="admin-bills">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <h1 className="font-serif-display text-4xl">Bills of Entry</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Record admission fees, medicine purchased and payments. Every bill is numbered automatically.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={exportMonth} data-testid="bills-export-btn" className="inline-flex items-center gap-2 rounded-full border border-forest text-forest px-5 py-2.5 text-sm hover:bg-forest hover:text-cream transition-all">
            <DownloadSimple size={16} weight="bold"/> Export PDF
          </button>
          <BillDialog onSaved={load} trigger={
            <button data-testid="add-bill-btn" className="inline-flex items-center gap-2 rounded-full bg-forest text-cream px-5 py-2.5 text-sm hover:bg-terracotta transition-all">
              <Plus size={16} weight="bold"/> New bill
            </button>
          }/>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Bills shown", value: stats.total },
          { label: "Gross billed", value: inr(stats.gross) },
          { label: "Collected", value: inr(stats.collected) },
          { label: "Outstanding", value: inr(stats.outstanding) },
        ].map(s => (
          <div key={s.label} className="bg-ivory border border-border/50 rounded-2xl p-5">
            <div className="overline">{s.label}</div>
            <div className="font-serif-display text-3xl mt-2 leading-tight">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by bill no, patient, phone, medicine…" className="bg-ivory h-11 pl-11" data-testid="bills-search"/>
        </div>
        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger className="w-full md:w-52 bg-ivory h-11" data-testid="bills-month-filter"><SelectValue placeholder="All months"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All months</SelectItem>
            {availableMonths.map(m => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 bg-ivory border border-border/50 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="p-4">Bill No</th>
              <th className="p-4">Date</th>
              <th className="p-4">Patient</th>
              <th className="p-4">Medicine</th>
              <th className="p-4 text-right">Admission</th>
              <th className="p-4 text-right">Med Cost</th>
              <th className="p-4 text-right">Paid</th>
              <th className="p-4 text-right">Balance</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map(b => (
              <tr key={b.id} className="border-t border-border/40 align-top" data-testid={`bill-${b.id}`}>
                <td className="p-4 font-mono text-xs whitespace-nowrap">{b.bill_no}</td>
                <td className="p-4 text-xs whitespace-nowrap">{b.bill_date}</td>
                <td className="p-4"><div className="font-medium">{b.patient_name}</div>{b.phone && <div className="text-xs text-muted-foreground">{b.phone}</div>}</td>
                <td className="p-4 text-xs">{b.medicine || <span className="text-muted-foreground">—</span>}{b.notes && <div className="text-muted-foreground italic line-clamp-1 mt-1">{b.notes}</div>}</td>
                <td className="p-4 text-right">{inr(b.admission_fee)}</td>
                <td className="p-4 text-right">{inr(b.medicine_cost)}</td>
                <td className="p-4 text-right text-sage font-medium">{inr(b.amount_paid)}</td>
                <td className={`p-4 text-right font-medium ${b.balance > 0 ? "text-terracotta" : "text-muted-foreground"}`}>{inr(b.balance)}</td>
                <td className="p-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <button onClick={() => printBill(b)} data-testid={`print-bill-${b.id}`} className="text-xs text-forest hover:text-terracotta inline-flex items-center gap-1">
                      <Receipt size={14}/> Receipt
                    </button>
                    <BillDialog initial={b} onSaved={load} trigger={
                      <button className="text-xs text-forest hover:text-terracotta inline-flex items-center gap-1" data-testid={`edit-bill-${b.id}`}>
                        <PencilSimple size={14}/> Edit
                      </button>
                    }/>
                    {isAdmin && (
                      <button onClick={() => remove(b)} data-testid={`delete-bill-${b.id}`} className="text-xs text-destructive hover:underline inline-flex items-center gap-1">
                        <Trash size={14}/> Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={9} className="p-10 text-center text-muted-foreground">{rows.length === 0 ? "No bills yet. Click \"New bill\" to create one." : "No results match your filter."}</td></tr>}
          </tbody>
        </table>
      </div>
      {filtered.length > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={filtered.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testId="bills-pagination"
        />
      )}
    </div>
  );
}
